/**
 * Production export: builds a ZIP containing one STL per piece + print settings + spec
 * Auto-splits text into chunks that respect the printer build volume (default 300×300×300 mm).
 */
import JSZip from 'jszip';
import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { buildAllFaceDxfFiles } from './dxfExport';

const FONT_URL = '/fonts/helvetiker_bold.typeface.json';
let fontPromise = null;
function getFont() {
  if (!fontPromise) {
    console.log('[ZIP] getFont: loading font from', FONT_URL);
    fontPromise = new Promise((resolve, reject) => {
      const loader = new FontLoader();
      loader.load(
        FONT_URL,
        (f) => { console.log('[ZIP] getFont: loaded'); resolve(f); },
        (e) => console.log('[ZIP] getFont: progress', e.loaded, '/', e.total),
        (err) => { console.error('[ZIP] getFont: ERROR', err); fontPromise = null; reject(err); },
      );
    });
  }
  return fontPromise;
}

/* ---------- Geometry builders (mm units, world-space STL) ---------- */
// Build solid letter geometry at REAL millimetre scale.
function buildSolidLettersGeo(font, text, heightMm, depthMm) {
  const geo = new TextGeometry(text || ' ', {
    font, size: heightMm, height: depthMm,
    curveSegments: 4, bevelEnabled: false,
  });
  geo.computeBoundingBox();
  return geo;
}

// Build hollow letter shell with optional INNER RIM (débord interne en bas)
// rim: { width, depth, clearance } in mm — produces a horizontal lip on which the face clips in
function buildHollowLettersGeo(font, text, heightMm, depthMm, wallThk, rim = null) {
  let Offset;
  try { Offset = require('polygon-offset'); } catch { Offset = null; }
  const shapes = font.generateShapes(text || ' ', heightMm);
  // Cache for rim use (we need both inner-offset and rim-offset polygons)
  const innerPolysPerShape = []; // [{shape, innerPolys: [[x,y]...] }]
  shapes.forEach((shape) => {
    if (!Offset) { innerPolysPerShape.push({ shape, innerPolys: [] }); return; }
    try {
      const pts = shape.getPoints(40);
      let path = pts.map((p) => [p.x, p.y]);
      if (path.length > 2) {
        const a = path[0], b = path[path.length - 1];
        if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) path.pop();
      }
      if (path.length < 4) { innerPolysPerShape.push({ shape, innerPolys: [] }); return; }
      const off = new Offset();
      const result = off.data([path]).padding(wallThk);
      if (!result || !result.length) { innerPolysPerShape.push({ shape, innerPolys: [] }); return; }
      result.forEach((poly) => {
        if (!poly || poly.length < 4) return;
        const reversed = poly.slice().reverse();
        const hp = new THREE.Path();
        hp.moveTo(reversed[0][0], reversed[0][1]);
        for (let i = 1; i < reversed.length; i++) hp.lineTo(reversed[i][0], reversed[i][1]);
        hp.closePath();
        shape.holes.push(hp);
      });
      innerPolysPerShape.push({ shape, innerPolys: result });
    } catch { innerPolysPerShape.push({ shape, innerPolys: [] }); }
  });
  const wallsGeo = new THREE.ExtrudeGeometry(shapes, { depth: depthMm, bevelEnabled: false, curveSegments: 4 });
  wallsGeo.computeBoundingBox();

  // Build rim (horizontal lip) if requested — a thin extrusion at the FRONT of the boîtier
  // The rim sits between the inner wall offset and (inner + rim.width) offset, on a thin slab of height rim.depth
  if (rim && rim.enabled && rim.width > 0 && Offset) {
    const rimShapes = [];
    innerPolysPerShape.forEach(({ innerPolys }) => {
      innerPolys.forEach((innerPoly) => {
        try {
          const innerClosed = innerPoly.slice();
          // Apply clearance: shrink the inner polygon a bit so the face fits with light play
          const off = new Offset();
          const inset = off.data([innerClosed]).padding(rim.width + (rim.clearance || 0));
          if (!inset || !inset.length) return;
          const outerRing = innerClosed; // outer edge of the rim slab = inner edge of the walls
          const rimShape = new THREE.Shape();
          rimShape.moveTo(outerRing[0][0], outerRing[0][1]);
          for (let i = 1; i < outerRing.length; i++) rimShape.lineTo(outerRing[i][0], outerRing[i][1]);
          rimShape.closePath();
          inset.forEach((hole) => {
            if (!hole || hole.length < 4) return;
            const rev = hole.slice().reverse();
            const hp = new THREE.Path();
            hp.moveTo(rev[0][0], rev[0][1]);
            for (let i = 1; i < rev.length; i++) hp.lineTo(rev[i][0], rev[i][1]);
            hp.closePath();
            rimShape.holes.push(hp);
          });
          rimShapes.push(rimShape);
        } catch { /* skip */ }
      });
    });
    if (rimShapes.length) {
      const rimGeo = new THREE.ExtrudeGeometry(rimShapes, { depth: rim.depth, bevelEnabled: false, curveSegments: 8 });
      // Place rim at the FRONT (positive Z) just before the front face plane
      rimGeo.translate(0, 0, depthMm - rim.depth);
      // Merge into wallsGeo
      const merged = mergeGeometries([wallsGeo, rimGeo]);
      wallsGeo.dispose();
      rimGeo.dispose();
      merged.computeBoundingBox();
      return merged;
    }
  }
  return wallsGeo;
}

// Simple geometry merger (BufferGeometryUtils.mergeGeometries works only with non-indexed buffers of same attrs)
function mergeGeometries(geos) {
  // Use three's util — import lazily
  // eslint-disable-next-line global-require
  const { mergeGeometries: m } = require('three/examples/jsm/utils/BufferGeometryUtils.js');
  return m(geos.map((g) => g.toNonIndexed ? g.toNonIndexed() : g));
}

// Add mounting holes + cable hole to a geometry by SUBTRACTING cylinders (via shape holes pre-merge approach)
// For simplicity in this MVP we DON'T do CSG subtraction (would need three-bvh-csg); instead, we generate
// separate small cylinders that the slicer can drill out manually OR we annotate them in the manifest.
function buildHolesGeo(holes) {
  if (!holes.length) return null;
  const grp = [];
  holes.forEach((h) => {
    const geo = new THREE.CylinderGeometry(h.diameter / 2, h.diameter / 2, h.length || 10, 24);
    geo.rotateX(Math.PI / 2);
    geo.translate(h.x, h.y, h.z || 0);
    grp.push(geo);
  });
  if (!grp.length) return null;
  return mergeGeometries(grp);
}

/* ---------- Auto-split text by build volume ---------- */
// Determine maximum chars per chunk so that chunk width ≤ maxWidthMm
function splitTextByVolume(font, text, heightMm, maxWidthMm) {
  if (!text || !text.trim()) return [];
  // Probe full-text width with TextGeometry
  const probe = new TextGeometry(text, { font, size: heightMm, height: 1, bevelEnabled: false, curveSegments: 6 });
  probe.computeBoundingBox();
  const totalW = (probe.boundingBox.max.x - probe.boundingBox.min.x);
  probe.dispose();
  if (totalW <= maxWidthMm) return [text];
  const avgCharW = totalW / text.length;
  const charsPerChunk = Math.max(1, Math.floor(maxWidthMm / avgCharW)); // exact fit, no safety margin
  // Prefer word boundaries
  const words = text.split(' ');
  const chunks = [];
  let current = '';
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (candidate.length <= charsPerChunk) {
      current = candidate;
    } else {
      if (current) chunks.push(current);
      // If single word is too long, split it
      if (w.length > charsPerChunk) {
        for (let i = 0; i < w.length; i += charsPerChunk) chunks.push(w.slice(i, i + charsPerChunk));
        current = '';
      } else {
        current = w;
      }
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

/* ---------- Mesh → STL ASCII string (JSZip-friendly) ---------- */
function geometryToStl(geo, name = 'part') {
  const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial());
  mesh.name = name;
  const exporter = new STLExporter();
  return exporter.parse(mesh, { binary: false });
}

/* ---------- Main: build production ZIP ---------- */
export async function exportProductionZip(state) {
  const { segments, dimensions, structureType, printSettings, printer, clips, standoffs, light, ledOn, socleRim, mountingHoles, cableHole, meta } = state;
  console.log('[ZIP] before getFont');
  const font = await getFont();
  console.log('[ZIP] after getFont, processing', segments?.length, 'segments');
  const heightMm = dimensions.height;
  const depthMm = dimensions.depth;
  const acrThkMm = dimensions.acrylicThickness || 4;
  const wallThkMm = dimensions.wallThickness || 3;
  const backThkMm = 3; // mm fixed for fond
  const maxWidthMm = Math.min(printer.maxX, printer.maxZ) - 10; // safety margin
  if (heightMm > printer.maxY) {
    throw new Error(`Hauteur ${heightMm}mm > volume imprimante Y=${printer.maxY}mm. Réduire la hauteur ou utiliser une imprimante plus grande.`);
  }

  const zip = new JSZip();
  const stlFolder = zip.folder('STL');
  const manifest = {
    project: meta?.name || 'Enseignes3D',
    client: meta?.client || '',
    reference: meta?.reference || '',
    install_address: meta?.installAddress || '',
    contact_email: meta?.contactEmail || '',
    contact_phone: meta?.contactPhone || '',
    deadline: meta?.deadlineDate || '',
    notes: meta?.notes || '',
    generated_at: new Date().toISOString(),
    structureType,
    printer_model: printer.id,
    dimensions_mm: dimensions,
    printer_build_volume_mm: printer,
    print_settings: printSettings,
    socle_rim: socleRim,
    mounting_holes: mountingHoles,
    cable_hole: { ...cableHole, applicable: !!ledOn },
    clips,
    standoffs,
    led: { enabled: ledOn, ...light },
    pieces: [],
  };

  const isBox = structureType === 'B';
  const pieceList = [];

  for (let s = 0; s < segments.length; s++) {
    const seg = segments[s];
    const safeSegName = seg.text.replace(/[^a-zA-Z0-9-_]/g, '_') || `segment${s + 1}`;
    // Detached segments go into their own subfolder (production_signs/sign_X/)
    const segFolder = seg.detached
      ? zip.folder(`detached_signs/sign_${s + 1}_${safeSegName}`).folder('STL')
      : stlFolder;
    const chunks = splitTextByVolume(font, seg.text, heightMm, maxWidthMm);
    chunks.forEach((chunk, ci) => {
      console.log('[ZIP] processing chunk', s, ci, chunk);
      const idx = chunks.length > 1 ? `_part${ci + 1}of${chunks.length}` : '';
      const baseName = `seg${s + 1}_${safeSegName}${idx}`;

      // --- Socle / Walls (PETG, hollow if boîtier, with optional rim) ---
      let wallsGeo;
      if (isBox) {
        wallsGeo = buildHollowLettersGeo(font, chunk, heightMm, depthMm, wallThkMm, socleRim);
      } else {
        wallsGeo = buildSolidLettersGeo(font, chunk, heightMm, depthMm);
      }
      const wallsBB = wallsGeo.boundingBox;
      const wallsSize = wallsBB ? { x: wallsBB.max.x - wallsBB.min.x, y: wallsBB.max.y - wallsBB.min.y, z: depthMm } : null;
      const wallsStl = geometryToStl(wallsGeo, `${baseName}_socle`);
      segFolder.file(`${baseName}_socle.stl`, wallsStl);
      pieceList.push({
        file: seg.detached
          ? `detached_signs/sign_${s + 1}_${safeSegName}/STL/${baseName}_socle.stl`
          : `STL/${baseName}_socle.stl`,
        detached: !!seg.detached,
        type: isBox ? 'Socle (parois + fond — boîtier creux)' : 'Parois',
        segment: seg.text,
        chunk,
        material: seg.materials.walls || 'petg',
        color: seg.colors.walls,
        size_mm: wallsSize,
        hollow: isBox,
        wall_thickness_mm: isBox ? wallThkMm : null,
        rim: isBox && socleRim?.enabled ? socleRim : null,
        mounting_holes: mountingHoles?.enabled ? mountingHoles : null,
        cable_hole: (cableHole?.enabled && ledOn) ? cableHole : null,
        // Standoff fixation positions on this piece (local mm coordinates from segment center)
        standoffs: standoffs?.enabled ? {
          count: Math.max(2, standoffs.count || 2),
          diameter_mm: standoffs.diameter,
          height_mm: standoffs.height,
          material: standoffs.material,
          hole_diameter_mm: standoffs.diameter + 0.5, // light press-fit
        } : null,
      });

      // --- Back plate (only if NOT boîtier — boîtier integrates fond into socle) ---
      if (!isBox) {
        const backGeo = buildSolidLettersGeo(font, chunk, heightMm, backThkMm);
        const backStl = geometryToStl(backGeo, `${baseName}_fond`);
        segFolder.file(`${baseName}_fond.stl`, backStl);
        pieceList.push({
          file: seg.detached
            ? `detached_signs/sign_${s + 1}_${safeSegName}/STL/${baseName}_fond.stl`
            : `STL/${baseName}_fond.stl`,
          detached: !!seg.detached,
          type: 'Fond (plaque arrière)',
          segment: seg.text,
          chunk,
          material: seg.materials.back || 'petg',
          color: seg.colors.back,
          size_mm: backGeo.boundingBox ? {
            x: backGeo.boundingBox.max.x - backGeo.boundingBox.min.x,
            y: backGeo.boundingBox.max.y - backGeo.boundingBox.min.y,
            z: backThkMm,
          } : null,
        });
      }

      // --- Face avant (PMMA — typically laser-cut, but STL provided for reference) ---
      const frontGeo = buildSolidLettersGeo(font, chunk, heightMm, acrThkMm);
      const frontStl = geometryToStl(frontGeo, `${baseName}_face`);
      segFolder.file(`${baseName}_face.stl`, frontStl);
      pieceList.push({
        file: seg.detached
          ? `detached_signs/sign_${s + 1}_${safeSegName}/STL/${baseName}_face.stl`
          : `STL/${baseName}_face.stl`,
        detached: !!seg.detached,
        type: 'Face avant',
        segment: seg.text,
        chunk,
        material: seg.materials.front || 'pmma',
        color: seg.colors.front,
        size_mm: frontGeo.boundingBox ? {
          x: frontGeo.boundingBox.max.x - frontGeo.boundingBox.min.x,
          y: frontGeo.boundingBox.max.y - frontGeo.boundingBox.min.y,
          z: acrThkMm,
        } : null,
        note: 'STL fourni pour visualisation 3D — utilisez le fichier DXF correspondant pour la découpe laser PMMA',
      });

      // Dispose
      wallsGeo.dispose();
      frontGeo.dispose();
    });
  }

  manifest.pieces = pieceList;

  // ===== DXF files for laser cutting of PMMA face avant =====
  console.log('[ZIP] building DXFs');
  const dxfFolder = zip.folder('DXF');
  const splitFn = (text) => splitTextByVolume(font, text, heightMm, maxWidthMm);
  const dxfList = buildAllFaceDxfFiles(font, segments, dimensions, splitFn);
  console.log('[ZIP] DXFs built:', dxfList.length);
  dxfList.forEach((d) => {
    const subFolder = d.detached
      ? zip.folder(d.detachedFolder).folder('DXF')
      : dxfFolder;
    subFolder.file(d.fileName, d.content);
  });
  manifest.dxf_face_files = dxfList.map((d) => ({
    file: d.detached ? `${d.detachedFolder}/DXF/${d.fileName}` : `DXF/${d.fileName}`,
    segment: d.segment,
    chunk: d.chunk,
    material: d.material,
    color: d.color,
    thickness_mm: d.thickness_mm,
  }));
  console.log('[ZIP] writing manifest + README');

  zip.file('print_settings.json', JSON.stringify(manifest, null, 2));

  // Human-readable README
  const readme = buildReadme(manifest);
  zip.file('README.txt', readme);

  console.log('[ZIP] generating ZIP blob...');
  const safeProj = (meta?.name || 'enseignes3d').replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 40);
  const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
  console.log('[ZIP] ZIP blob ready, size:', blob.size);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeProj}_production_${Date.now()}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { pieceCount: pieceList.length, fileName: a.download };
}

function buildReadme(m) {
  const ps = m.print_settings;
  const lines = [];
  lines.push('========================================================');
  lines.push('  ENSEIGNES3D.FR — DOSSIER DE PRODUCTION');
  lines.push('========================================================');
  lines.push(`Projet  : ${m.project}`);
  if (m.reference)       lines.push(`Réf.    : ${m.reference}`);
  if (m.client)          lines.push(`Client  : ${m.client}`);
  if (m.install_address) lines.push(`Install : ${m.install_address}`);
  if (m.contact_email || m.contact_phone) {
    lines.push(`Contact : ${[m.contact_email, m.contact_phone].filter(Boolean).join(' • ')}`);
  }
  if (m.deadline)        lines.push(`Échéance: ${m.deadline}`);
  lines.push(`Généré  : ${m.generated_at}`);
  lines.push(`Type de structure : ${m.structureType}`);
  lines.push(`Imprimante : ${m.printer_model?.toUpperCase() || 'générique'}`);
  lines.push(`Dimensions enveloppe : H=${m.dimensions_mm.height} L=auto P=${m.dimensions_mm.depth} mm`);
  lines.push(`Volume imprimante : ${m.printer_build_volume_mm.maxX} × ${m.printer_build_volume_mm.maxY} × ${m.printer_build_volume_mm.maxZ} mm`);
  lines.push('');
  lines.push('--- PARAMÈTRES SLICER (FDM) ---');
  lines.push(`Buse                   : ${ps.nozzleDiameter} mm`);
  lines.push(`Hauteur de couche      : ${ps.layerHeight} mm (1ère couche : ${ps.firstLayerHeight} mm)`);
  lines.push(`Parois (perimeters)    : ${ps.wallLineCount}`);
  lines.push(`Couches haut/bas       : ${ps.topBottomLayers}`);
  lines.push(`Remplissage            : ${ps.infillPercent}% (${ps.infillPattern})`);
  lines.push(`Vitesse impression     : ${ps.printSpeed} mm/s`);
  lines.push(`Vitesse 1ère couche    : ${ps.firstLayerSpeed} mm/s`);
  lines.push(`Vitesse déplacements   : ${ps.travelSpeed} mm/s`);
  lines.push(`Température buse       : ${ps.nozzleTemp} °C`);
  lines.push(`Température plateau    : ${ps.bedTemp} °C`);
  lines.push(`Adhérence              : ${ps.adhesion}${ps.brimWidth ? ` (brim ${ps.brimWidth}mm)` : ''}`);
  lines.push(`Supports               : ${ps.enableSupports ? `oui (angle ${ps.supportAngle}°)` : 'non'}`);
  lines.push(`Rétraction             : ${ps.retractionEnabled ? `${ps.retractionDistance}mm @ ${ps.retractionSpeed}mm/s` : 'désactivée'}`);
  lines.push(`Ventilateur            : ${ps.fanSpeed}%`);
  lines.push(`Filament               : ${ps.filamentBrand} (Ø ${ps.filamentDiameter} mm)`);
  lines.push('');
  if (m.socle_rim?.enabled) {
    lines.push('--- DÉBORD SOCLE (clipsage face) ---');
    lines.push(`Largeur débord         : ${m.socle_rim.width} mm`);
    lines.push(`Épaisseur débord       : ${m.socle_rim.depth} mm`);
    lines.push(`Jeu d'insertion        : ${m.socle_rim.clearance} mm`);
    lines.push('  → Le socle inclut un débord intérieur sur lequel la face PMMA se clipse');
    lines.push('');
  }
  if (m.mounting_holes?.enabled) {
    lines.push('--- TROUS DE FIXATION ---');
    lines.push(`Disposition            : ${m.mounting_holes.pattern}`);
    lines.push(`Ø trou (vis)           : ${m.mounting_holes.diameter} mm`);
    if (m.mounting_holes.countersink) {
      lines.push(`Lamage                 : Ø${m.mounting_holes.countersinkDiameter} × ${m.mounting_holes.countersinkDepth} mm`);
    }
    lines.push('  → À percer en post-impression (forêt ou cône inclus dans le slicer)');
    lines.push('');
  }
  if (m.cable_hole?.enabled && m.cable_hole?.applicable) {
    lines.push('--- PASSAGE CÂBLE LED ---');
    lines.push(`Ø trou                 : ${m.cable_hole.diameter} mm`);
    lines.push(`Position               : ${m.cable_hole.position}`);
    lines.push('  → À percer en post-impression au niveau indiqué');
    lines.push('');
  }
  lines.push('--- PIÈCES À IMPRIMER ---');
  m.pieces.forEach((p, i) => {
    lines.push(`${i + 1}. ${p.file}`);
    lines.push(`   ${p.type} — segment "${p.segment}"${p.chunk !== p.segment ? ` → portion "${p.chunk}"` : ''}`);
    lines.push(`   Matière : ${p.material.toUpperCase()}  Couleur : ${p.color}`);
    if (p.size_mm) lines.push(`   Encombrement : ${p.size_mm.x.toFixed(1)} × ${p.size_mm.y.toFixed(1)} × ${p.size_mm.z.toFixed(1)} mm`);
    if (p.hollow) lines.push(`   ▸ Boîtier CREUX (épaisseur paroi ${p.wall_thickness_mm} mm) — slicer = parois pleines, infill ${ps.infillPercent}%`);
    if (p.rim) lines.push(`   ▸ Débord interne ${p.rim.width}×${p.rim.depth} mm pour clipsage face`);
    if (p.standoffs) {
      lines.push(`   ▸ Entretoises : ${p.standoffs.count}× ${p.standoffs.material} Ø${p.standoffs.diameter_mm} mm h=${p.standoffs.height_mm} mm`);
      lines.push(`     Trous fond Ø${p.standoffs.hole_diameter_mm} mm (régulièrement répartis sur la base, axe Y bas)`);
    }
    if (p.mounting_holes) lines.push(`   ▸ Trous de fixation ${p.mounting_holes.pattern} Ø${p.mounting_holes.diameter} mm à percer`);
    if (p.cable_hole) lines.push(`   ▸ Trou câble Ø${p.cable_hole.diameter} mm — position ${p.cable_hole.position}`);
    if (p.note) lines.push(`   ▸ ${p.note}`);
    lines.push('');
  });
  lines.push('--- ASSEMBLAGE ---');
  lines.push(`Clips : ${m.clips.enabled ? `${m.clips.count} × clip ${m.clips.type}` : 'non'}`);
  lines.push(`Entretoises : ${m.standoffs.enabled ? `${Math.max(2, m.standoffs.count || 2)} par élément, ${m.standoffs.material} h=${m.standoffs.height}mm Ø${m.standoffs.diameter}mm — collées à la base du socle` : 'non'}`);
  lines.push(`LEDs : ${m.led.enabled ? `oui — densité ${m.led.density || '?'} LED/m, ${m.led.color || '?'} K` : 'non'}`);
  lines.push('');
  lines.push('========================================================');
  lines.push('Importer chaque STL dans votre slicer (PrusaSlicer / Cura / OrcaSlicer / Creality Print)');
  lines.push('avec les paramètres ci-dessus. Les boîtiers creux doivent être ');
  lines.push('imprimés avec "Spiral Vase = NON" et "Walls = parois pleines".');
  lines.push('');
  lines.push('--- DÉCOUPE LASER PMMA (FACE AVANT) ---');
  lines.push('Les fichiers DXF du dossier /DXF (et /detached_signs/*/DXF) contiennent');
  lines.push('les contours de chaque face avant pour découpe laser en PMMA :');
  lines.push('  • Layer "CUT" (rouge)     = traits de découpe (contour extérieur + contre-formes)');
  lines.push('  • Layer "ENGRAVE" (bleu)  = étiquetage à graver en bas-relief (optionnel)');
  lines.push('Unités : millimètres. Compatible LightBurn / RDWorks / EzCad / Cura Engrave / Inkscape.');
  lines.push('Épaisseur PMMA suggérée : voir manifest pour chaque face.');
  lines.push('========================================================');
  return lines.join('\n');
}
